import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  TablePagination,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
  FilterList as FilterListIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import ProductMappingModal from './ProductMappingModal';
import { useAuth } from '../../../contexts/AuthContext';
import { nodeApi, n11Api, accountingApi, hepsiburadaApi, trendyolMpApi, pazaramaApi, pttavmApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

// Pazaryeri Ürün Tipi
interface MarketplaceProduct {
  id: string;
  title: string;
  price: number;
  currency: string;
  stockQuantity: number;
  categoryName: string;
  brand: string;
  barcode?: string;
  sku?: string;
  imageUrl?: string;
  status: 'active' | 'inactive' | 'out_of_stock';
  lastUpdated: string;
  contentId?: string;
  productMainId?: string;
  variantId?: string;
  stockLastModifiedDate?: string;
}

// Platform türleri
type MarketplacePlatform = 'trendyol' | 'n11' | 'hepsiburada' | 'pazarama' | 'pttavm';
type FoodPlatform = 'yemeksepeti' | 'getir' | 'migros_yemek' | 'trendyol_yemek';
type Platform = MarketplacePlatform | FoodPlatform;

// Entegrasyon Tipi
interface IntegrationData {
  id: number | string;
  platform: Platform;
  category?: 'marketplace' | 'food';
  branchId?: string | null;
  branchName?: string | null;
  isEnabled?: boolean;
  enabled?: boolean;
  // API Response alanları
  apiKey?: string | null;
  apiSecret?: string | null;
  sellerId?: string | null;
  restaurantId?: string | null;
  appSecretKey?: string | null;
  restaurantSecretKey?: string | null;
  isConnected?: boolean;
  lastSyncAt?: string | null;
  errorCount?: number;
  // Legacy uyumluluk
  credentials?: {
    apiKey?: string;
    apiSecret?: string;
    sellerId?: string;
    restaurantId?: string;
    appSecretKey?: string;
    restaurantSecretKey?: string;
    lastUpdated?: string;
  };
  settings?: {
    categories?: any;
  };
  status?: {
    connected: boolean;
    lastSync: string;
    errorCount: number;
  };
}

// Mevcut Stok Ürün Tipi
interface InventoryProduct {
  id: string;
  name: string;
  barcode?: string;
  stockCode: string;
  quantity: number;
  unit: string;
  salePrice: number;
  saleCurrency?: string;
  categoryId?: string;
  brandId?: string;
  warehouseId?: string;
  isActive: boolean;
  imageUrl?: string;
  isMainItem?: boolean;
  parentItemId?: string;
}

// Depo Tipi
interface Warehouse {
  id: string;
  name: string;
  isDefault?: boolean;
}

interface MarketplaceProductsModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  integration: IntegrationData;
}

const MarketplaceProductsModal: React.FC<MarketplaceProductsModalProps> = ({
  open,
  onClose,
  companyId,
  integration
}) => {
  const { t, i18n } = useTranslation('salesSystem');
  const currentLocale = i18n.language?.startsWith('tr') ? 'tr-TR' : 'en-US';
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al
  const { warehouses } = useAuth();
  
  const [products, setProducts] = useState<MarketplaceProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState<MarketplaceProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  
  // Ürün eşleştirme modalı için state'ler
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  const [inventoryProducts, setInventoryProducts] = useState<InventoryProduct[]>([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  
  // Platform-specific API Filtreleri
  const [filters, setFilters] = useState({
    // Ortak filtreler
    id: '',
    productMainId: '',
    stockCode: '',
    brandName: '',
    barcode: '',
    page: 0,
    size: 100,
    
    // N11 özel filtreleri
    saleStatus: '',
    productStatus: '',
    categoryIds: '',
    
    // Trendyol özel filtreleri (dokümantasyona göre)
    approved: '',
    archived: '',
    productCode: '',
    categoryId: '',
    onSale: '',
    rejected: '',
    blacklisted: '',
    hasActiveCampaign: '',
    startDate: '',
    endDate: '',
    nextPageToken: '',
    orderByDirection: ''
  });
  
  // Sayfalama
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [batchRequestId, setBatchRequestId] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchSummary, setBatchSummary] = useState<string | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogSummary, setCatalogSummary] = useState<string | null>(null);
  const [trendyolNextPageToken, setTrendyolNextPageToken] = useState('');
  const [trendyolTotalElements, setTrendyolTotalElements] = useState<number | null>(null);

  // Platform bilgilerini al
  const getPlatformInfo = (platform: string) => {
    switch (platform) {
      case 'n11':
        return { name: 'N11', color: '#ff6600' };
      case 'hepsiburada':
        return { name: 'Hepsiburada', color: '#ff6000' };
      case 'trendyol':
        return { name: 'Trendyol', color: '#f27a1a' };
      case 'pazarama':
        return { name: 'Pazarama', color: '#e6007e' };
      case 'pttavm':
        return { name: 'PttAVM', color: '#e30613' };
      case 'getir':
        return { name: 'Getir', color: '#5d3ebc' };
      default:
        return { name: platform, color: '#1976d2' };
    }
  };

  const platformInfo = getPlatformInfo(integration.platform);

  const normalizeTrendyolProducts = (rawData: any): {
    products: MarketplaceProduct[];
    nextPageToken: string;
    totalElements: number | null;
  } => {
    const source = rawData?.data ?? rawData;
    const content = Array.isArray(source?.content)
      ? source.content
      : (Array.isArray(source) ? source : []);

    const nextPageToken = typeof source?.nextPageToken === 'string' ? source.nextPageToken : '';
    const totalElements = Number.isFinite(Number(source?.totalElements)) ? Number(source.totalElements) : null;

    const products = content.flatMap((item: any, index: number) => {
      const contentId = item?.contentId != null ? String(item.contentId) : undefined;
      const productMainId = item?.productMainId != null ? String(item.productMainId) : undefined;
      const variants = Array.isArray(item?.variants) ? item.variants : [];

      if (variants.length > 0) {
        return variants.map((variant: any, variantIndex: number) => {
          const variantId = variant?.variantId != null ? String(variant.variantId) : undefined;
          const barcode = variant?.barcode || item?.barcode || undefined;
          const stockCode = variant?.stockCode || item?.stockCode || undefined;
          const quantity = Number(variant?.quantity ?? item?.quantity ?? 0);
          const salePrice = Number(variant?.salePrice ?? item?.salePrice ?? 0);
          const listPrice = Number(variant?.listPrice ?? item?.listPrice ?? salePrice);
          const stockLastModifiedDate = variant?.stockLastModifiedDate != null
            ? new Date(Number(variant.stockLastModifiedDate)).toISOString()
            : undefined;

          return {
            id: (variantId || barcode || stockCode || `trendyol-${index}-${variantIndex}`).toString(),
            title: item?.title || item?.name || item?.productName || productMainId || contentId || 'Trendyol Product',
            price: salePrice || listPrice || 0,
            currency: item?.currencyType || item?.currency || 'TRY',
            stockQuantity: quantity,
            categoryName: item?.categoryName || item?.category?.name || '-',
            brand: item?.brand || item?.brandName || '-',
            barcode,
            sku: stockCode,
            imageUrl: Array.isArray(item?.images) && item.images.length > 0
              ? (item.images[0]?.url || item.images[0])
              : item?.imageUrl,
            status: quantity > 0 ? 'active' : 'out_of_stock',
            lastUpdated: stockLastModifiedDate || item?.lastUpdateDate || item?.lastModifiedDate || new Date().toISOString(),
            contentId,
            productMainId,
            variantId,
            stockLastModifiedDate
          } as MarketplaceProduct;
        });
      }

      return [{
        id: (item?.id || item?.barcode || item?.stockCode || `trendyol-${index}`).toString(),
        title: item?.title || item?.name || item?.productName || productMainId || contentId || 'Trendyol Product',
        price: Number(item?.salePrice ?? item?.price ?? item?.listPrice ?? 0),
        currency: item?.currencyType || item?.currency || 'TRY',
        stockQuantity: Number(item?.quantity ?? item?.stockQuantity ?? item?.stock ?? 0),
        categoryName: item?.categoryName || item?.category?.name || '-',
        brand: item?.brand || item?.brandName || '-',
        barcode: item?.barcode || undefined,
        sku: item?.stockCode || item?.merchantSku || undefined,
        imageUrl: Array.isArray(item?.images) && item.images.length > 0
          ? (item.images[0]?.url || item.images[0])
          : item?.imageUrl,
        status: Number(item?.quantity ?? 0) > 0 ? 'active' : 'out_of_stock',
        lastUpdated: item?.lastUpdateDate || item?.lastModifiedDate || new Date().toISOString(),
        contentId,
        productMainId
      }];
    });

    return { products, nextPageToken, totalElements };
  };

  const normalizeHepsiburadaProducts = (rawData: any): MarketplaceProduct[] => {
    const payload = rawData?.listings || rawData?.rows || rawData?.data || rawData;
    const content = Array.isArray(payload)
      ? payload
      : (Array.isArray(payload?.listings)
        ? payload.listings
        : (Array.isArray(payload?.rows)
          ? payload.rows
          : (Array.isArray(payload?.items)
            ? payload.items
            : (Array.isArray(payload?.content) ? payload.content : []))));

    return content.map((item: any, index: number) => {
      const stock = Number(item?.availableStock ?? item?.stock ?? item?.stockQuantity ?? item?.quantity ?? 0);
      const rawStatus = (item?.status || item?.listingStatus || '').toString().toLowerCase();
      const isSalable = item?.isSalable === true;

      return {
        id: (item?.listingId || item?.id || item?.hepsiburadaSku || item?.hbSku || item?.merchantSku || item?.sku || `hepsiburada-${index}`).toString(),
        title: item?.title || item?.name || item?.productName || item?.listingTitle || item?.merchantSku || item?.hepsiburadaSku || 'Hepsiburada Product',
        price: Number(item?.salePrice ?? item?.price ?? item?.listPrice ?? 0),
        currency: item?.currency || item?.currencyCode || 'TRY',
        stockQuantity: stock,
        categoryName: item?.categoryName || item?.category?.name || '-',
        brand: item?.brand || item?.brandName || '-',
        barcode: item?.barcode || undefined,
        sku: item?.merchantSku || item?.hepsiburadaSku || item?.hbSku || item?.sku || undefined,
        imageUrl: item?.imageUrl || item?.image || undefined,
        status: rawStatus.includes('passive') || rawStatus.includes('inactive') || rawStatus.includes('deactiv')
          ? 'inactive'
          : (isSalable || stock > 0 ? 'active' : 'out_of_stock'),
        lastUpdated: item?.lastUpdateDate || item?.lastModifiedDate || item?.updatedAt || new Date().toISOString()
      };
    });
  };

  const normalizePazaramaProducts = (rawData: any): MarketplaceProduct[] => {
    const content = Array.isArray(rawData)
      ? rawData
      : (Array.isArray(rawData?.items)
        ? rawData.items
        : (Array.isArray(rawData?.content)
          ? rawData.content
          : (Array.isArray(rawData?.products) ? rawData.products : [])));

    return content.map((item: any, index: number) => {
      const stock = Number(item?.stockCount ?? item?.stock ?? item?.quantity ?? 0);
      return {
        id: (item?.code || item?.barcode || item?.stockCode || `pazarama-${index}`).toString(),
        title: item?.name || item?.displayName || item?.productName || 'Pazarama Product',
        price: Number(item?.salePrice ?? item?.listPrice ?? item?.price ?? 0),
        currency: item?.currency || 'TRY',
        stockQuantity: stock,
        categoryName: item?.categoryName || item?.category?.name || '-',
        brand: item?.brandName || item?.brand || '-',
        barcode: item?.barcode || item?.code || undefined,
        sku: item?.stockCode || item?.code || undefined,
        imageUrl: Array.isArray(item?.images) && item.images.length > 0
          ? (item.images[0]?.url || item.images[0])
          : (item?.imageUrl || item?.image || undefined),
        status: stock > 0 ? 'active' : 'out_of_stock',
        lastUpdated: item?.lastUpdateDate || item?.lastModifiedDate || item?.updatedAt || new Date().toISOString()
      } as MarketplaceProduct;
    });
  };

  const normalizePttAvmProducts = (rawData: any): MarketplaceProduct[] => {
    const content = Array.isArray(rawData)
      ? rawData
      : (Array.isArray(rawData?.items)
        ? rawData.items
        : (Array.isArray(rawData?.content) ? rawData.content : []));

    return content.map((item: any, index: number) => {
      const stock = Number(item?.miktar ?? item?.Miktar ?? item?.quantity ?? 0);
      const price = Number(item?.kdVli ?? item?.KDVli ?? item?.kdVsiz ?? item?.KDVsiz ?? 0);
      const barkod = item?.barkod || item?.Barkod || item?.barcode || undefined;
      const urunId = item?.urunId ?? item?.UrunId;
      const isActive = item?.aktif ?? item?.isActive ?? item?.Aktif;
      return {
        id: (barkod || (urunId != null ? String(urunId) : undefined) || `pttavm-${index}`).toString(),
        title: item?.urunAdi || item?.UrunAdi || item?.name || 'PttAVM Product',
        price,
        currency: 'TRY',
        stockQuantity: stock,
        categoryName: item?.altKategoriAdi || item?.tedarikciAltKategoriAdi || '-',
        brand: '-',
        barcode: barkod,
        sku: barkod || (urunId != null ? String(urunId) : undefined),
        imageUrl: item?.resim1Url || item?.resimListesi?.[0]?.url || undefined,
        status: isActive === false ? 'inactive' : (stock > 0 ? 'active' : 'out_of_stock'),
        lastUpdated: item?.updatedAt || new Date().toISOString()
      } as MarketplaceProduct;
    });
  };

  const normalizeN11Products = (rawData: any): MarketplaceProduct[] => {
    const content = Array.isArray(rawData)
      ? rawData
      : (Array.isArray(rawData?.items)
        ? rawData.items
        : (Array.isArray(rawData?.content) ? rawData.content : []));

    return content.map((item: any, index: number) => {
      const stock = Number(item?.quantity ?? item?.stockQuantity ?? item?.stock ?? 0);
      const price = Number(item?.salePrice ?? item?.price ?? item?.listPrice ?? 0);
      const rawStatus = (item?.status || item?.productStatus || '').toString().toLowerCase();

      return {
        id: (item?.id || item?.productId || item?.stockCode || item?.barcode || `n11-${index}`).toString(),
        title: item?.title || item?.name || item?.productName || 'N11 Product',
        price: Number.isFinite(price) ? price : 0,
        currency: item?.currency || item?.currencyType || 'TRY',
        stockQuantity: Number.isFinite(stock) ? stock : 0,
        categoryName: item?.categoryName || item?.category?.name || '-',
        brand: item?.brand || item?.brandName || '-',
        barcode: item?.barcode || undefined,
        sku: item?.stockCode || item?.sku || undefined,
        imageUrl: item?.imageUrl || item?.image || undefined,
        status: rawStatus.includes('passive') || rawStatus.includes('inactive')
          ? 'inactive'
          : (Number.isFinite(stock) && stock > 0 ? 'active' : 'out_of_stock'),
        lastUpdated: item?.lastUpdateDate || item?.lastModifiedDate || item?.updatedAt || new Date().toISOString()
      } as MarketplaceProduct;
    });
  };

  const trendyolGetWithLocalFallback = async (pathWithQuery: string) => {
    try {
      return await trendyolMpApi.get(pathWithQuery);
    } catch (primaryError: any) {
      if (primaryError?.response?.status !== 404) {
        throw primaryError;
      }

      const localUrl = `http://localhost:5018${pathWithQuery}`;
      const localResponse = await trendyolMpApi.get(localUrl);
      setError('Canli Trendyol API endpointi 404 dondugu icin lokal backend kullanildi (localhost:5018).');
      return localResponse;
    }
  };

  // Ürünleri yükle
  const fetchProducts = async (customFilters?: any) => {
    if (!companyId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Filtreleri query parametrelerine çevir
      const activeFilters = customFilters || filters;
      const queryParams = new URLSearchParams();
      
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
      
      const isN11 = integration.platform === 'n11';
      const isTrendyol = integration.platform === 'trendyol';
      const isHepsiburada = integration.platform === 'hepsiburada';
      const isPazarama = integration.platform === 'pazarama';
      const isPttavm = integration.platform === 'pttavm';

      if (isTrendyol) {
        if (activeFilters.nextPageToken) {
          queryParams.delete('page');
        }
        const shouldQueryUnapproved = activeFilters.approved === 'false' || activeFilters.rejected === 'true';
        const trendyolPath = shouldQueryUnapproved
          ? '/api/TrendyolProductIntegration/search/unapproved'
          : '/api/TrendyolProductIntegration/search/approved';
        const trendyolResponse = await trendyolGetWithLocalFallback(`${trendyolPath}?${queryParams.toString()}`);
        const normalized = normalizeTrendyolProducts(trendyolResponse.data?.data);
        setProducts(normalized.products);
        setTrendyolNextPageToken(normalized.nextPageToken);
        setTrendyolTotalElements(normalized.totalElements);
      } else if (isHepsiburada) {
        setTrendyolNextPageToken('');
        setTrendyolTotalElements(null);
        const page = Number(activeFilters.page ?? 0);
        const pageSize = Number(activeFilters.size ?? 100);
        const hbResponse = await hepsiburadaApi.get(`/api/hepsiburada/listings?page=${page}&pageSize=${pageSize}`);
        const hbEnvelope = hbResponse.data?.Data || hbResponse.data?.data || hbResponse.data;
        const hbRoot = hbEnvelope?.data || hbEnvelope;
        const normalized = normalizeHepsiburadaProducts(hbRoot);
        setProducts(normalized);
        setTrendyolTotalElements(
          Number(hbRoot?.totalCount ?? hbEnvelope?.totalCount ?? normalized.length) || normalized.length
        );
      } else if (isPazarama) {
        setTrendyolNextPageToken('');
        setTrendyolTotalElements(null);
        const page = Number(activeFilters.page ?? 1);
        const size = Number(activeFilters.size ?? 100);
        const pzResponse = await pazaramaApi.get(`/api/pazarama/products?page=${page}&size=${size}`);
        const pzRoot = pzResponse.data?.data || pzResponse.data?.Data || pzResponse.data;
        const normalized = normalizePazaramaProducts(pzRoot);
        setProducts(normalized);
      } else if (isPttavm) {
        setTrendyolNextPageToken('');
        setTrendyolTotalElements(null);
        const searchPage = Number(activeFilters.page ?? 1);
        const categoryId = Number(activeFilters.categoryId ?? 0);
        const subCategoryId = Number(activeFilters.subCategoryId ?? 0);
        const isActive = activeFilters.isActive !== 'false';
        const isInStock = activeFilters.isInStock !== 'false';
        const merchantCategoryId = Number(activeFilters.merchantCategoryId ?? 0);
        const pttResponse = await pttavmApi.get(
          `/api/pttavm/products/search?categoryId=${categoryId}&subCategoryId=${subCategoryId}` +
          `&isActive=${isActive}&isInStock=${isInStock}&merchantCategoryId=${merchantCategoryId}&searchPage=${searchPage}`
        );
        const pttRoot = pttResponse.data?.data || pttResponse.data?.Data || pttResponse.data;
        const normalized = normalizePttAvmProducts(pttRoot);
        setProducts(normalized);
        setTrendyolTotalElements(normalized.length);
      } else {
        setTrendyolNextPageToken('');
        setTrendyolTotalElements(null);
        const basePath = isN11
          ? '/api/n11/products'
          : `/api/integrations/${integration.platform}/products`;
        const response = isN11
          ? await n11Api.get(`${basePath}?${queryParams.toString()}`)
          : await nodeApi.get(`${basePath}?${queryParams.toString()}`);
        const data = response.data;
        const rawProducts = data?.products || [];
        setProducts(isN11 ? normalizeN11Products(rawProducts) : rawProducts);
      }
    } catch (error: any) {
      const errorData = error.response?.data;
      const trendyolErrors = errorData?.data?.errors;
      const trendyolAuthError = Array.isArray(trendyolErrors)
        ? trendyolErrors.find((e: any) => e?.key === 'authentication.error')
        : null;

      if (error.response?.status === 401 || trendyolAuthError) {
        setError(
          `Trendyol kimlik dogrulama hatasi (401). API key/secret, sellerId veya stage yetkisini kontrol edin. ${
            trendyolAuthError?.message || ''
          }`.trim()
        );
        setProducts([]);
        return;
      }

      if (errorData?.error === 'RATE_LIMIT_EXCEEDED') {
        setError(t('marketplaceProductsModal.errors.rateLimitExceeded', { message: errorData.message }));
        setProducts([]);
        return;
      }
      if (errorData?.error === 'CLOUDFLARE_BLOCKED') {
        setError(t('marketplaceProductsModal.errors.apiAccessBlocked', { message: errorData.message }));
        setProducts([]);
        return;
      }
      console.error('Error while loading products:', error);
      setError(error instanceof Error ? error.message : t('marketplaceProductsModal.errors.loadProducts'));
    } finally {
      setLoading(false);
    }
  };

  // Bağlantı testi yap (kullanılmıyor artık)
  const testConnection = async () => {
    // Bu fonksiyon artık kullanılmıyor, fetchProducts kullanılıyor
  };

  // Modal açıldığında otomatik ürün getirme - kaldırıldı
  // Artık kullanıcı filtreleri seçtikten sonra manuel olarak ürünleri getirecek

  // Filtre değişikliği
  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Filtreleri temizle
  const clearFilters = () => {
    setFilters({
      // Ortak filtreler
      id: '',
      productMainId: '',
      stockCode: '',
      brandName: '',
      barcode: '',
      page: 0,
      size: 100,
      
      // N11 özel filtreleri
      saleStatus: '',
      productStatus: '',
      categoryIds: '',
      
      // Trendyol özel filtreleri (dokümantasyona göre)
      approved: '',
      archived: '',
      productCode: '',
      categoryId: '',
      onSale: '',
      rejected: '',
      blacklisted: '',
      hasActiveCampaign: '',
      startDate: '',
      endDate: '',
      nextPageToken: '',
      orderByDirection: ''
    });
    setProducts([]);
    setSelectedProducts([]);
    setTrendyolNextPageToken('');
    setTrendyolTotalElements(null);
  };

  // Son istek zamanını takip et
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const MIN_REQUEST_INTERVAL = 5000; // 5 saniye minimum aralık

  // Filtreleri uygula ve ürünleri getir
  const applyFilters = () => {
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime;
    
    if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
      const waitTime = Math.ceil((MIN_REQUEST_INTERVAL - timeSinceLastRequest) / 1000);
      setError(`⏱️ You are sending requests too fast. Please wait ${waitTime} seconds.`);
      return;
    }
    
    setLastRequestTime(now);
    fetchProducts();
  };

  const runCatalogLookupQuickCheck = async () => {
    if (integration.platform !== 'trendyol') return;
    setCatalogLoading(true);
    setCatalogSummary(null);
    try {
      const [brandResp, catResp] = await Promise.all([
        trendyolGetWithLocalFallback('/api/TrendyolProductIntegration/catalog/brands?page=0&size=20'),
        trendyolGetWithLocalFallback('/api/TrendyolProductIntegration/catalog/categories')
      ]);

      const brandCount = Array.isArray(brandResp.data?.data?.brands)
        ? brandResp.data.data.brands.length
        : (Array.isArray(brandResp.data?.data?.content) ? brandResp.data.data.content.length : 0);
      const categoryCount = Array.isArray(catResp.data?.data?.categories)
        ? catResp.data.data.categories.length
        : (Array.isArray(catResp.data?.data?.categoryTree) ? catResp.data.data.categoryTree.length : 0);

      setCatalogSummary(`Catalog kontrol tamam: brands=${brandCount}, categories=${categoryCount}`);
    } catch (lookupError: any) {
      if (lookupError?.response?.status === 404) {
        setCatalogSummary('Catalog kontrol hatasi: TrendyolProductIntegration endpointleri bu ortamda deploy edilmemis (404).');
      } else {
        setCatalogSummary(`Catalog kontrol hatası: ${lookupError?.response?.data?.message || lookupError?.message || 'Bilinmeyen hata'}`);
      }
    } finally {
      setCatalogLoading(false);
    }
  };

  const queryBatchResult = async () => {
    if (integration.platform !== 'trendyol') return;
    if (!batchRequestId.trim()) {
      setError('Batch Request ID zorunludur');
      return;
    }

    setBatchLoading(true);
    setBatchSummary(null);
    try {
      const response = await trendyolGetWithLocalFallback(`/api/TrendyolProductIntegration/batch/${encodeURIComponent(batchRequestId.trim())}`);
      const batchData = response.data?.data;
      const status = batchData?.status ?? batchData?.batchStatus ?? 'UNKNOWN';
      const failedCount = Array.isArray(batchData?.items)
        ? batchData.items.filter((x: any) => !!x?.failureReasons?.length).length
        : 0;
      setBatchSummary(`Batch status=${status}, failedItems=${failedCount}`);
    } catch (batchError: any) {
      if (batchError?.response?.status === 404) {
        setBatchSummary('Batch sorgu hatasi: TrendyolProductIntegration endpointleri bu ortamda deploy edilmemis (404).');
      } else {
        setBatchSummary(`Batch sorgu hatası: ${batchError?.response?.data?.message || batchError?.message || 'Bilinmeyen hata'}`);
      }
    } finally {
      setBatchLoading(false);
    }
  };

  // Arama filtresi
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProducts(products);
    } else {
      const normalizedSearch = searchTerm.toLowerCase();
      const filtered = products.filter(product =>
        (product.title || '').toLowerCase().includes(normalizedSearch) ||
        (product.brand || '').toLowerCase().includes(normalizedSearch) ||
        (product.categoryName || '').toLowerCase().includes(normalizedSearch) ||
        (product.barcode || '').includes(searchTerm) ||
        (product.sku || '').toLowerCase().includes(normalizedSearch)
      );
      setFilteredProducts(filtered);
    }
    setPage(0); // Arama yapıldığında sayfa sıfırla
  }, [searchTerm, products]);

  // Ürün seçimi
  const handleSelectProduct = (productId: string) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // Tümünü seç/seçimi kaldır
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const currentPageProductIds = paginatedProducts.map(product => product.id);
      setSelectedProducts(prev => {
        const combined = [...prev, ...currentPageProductIds];
        return Array.from(new Set(combined));
      });
    } else {
      const currentPageProductIds = paginatedProducts.map(product => product.id);
      setSelectedProducts(prev => prev.filter(id => !currentPageProductIds.includes(id)));
    }
  };

  // Mevcut stok ürünlerini yükle (SQL API'den)
  const loadInventoryProducts = async () => {
    if (!companyId) return;
    
    setLoadingInventory(true);
    try {
      console.log('📦 [INVENTORY] SQL API\'den stok ürünleri yükleniyor...');
      
      const response = await accountingApi.get('/api/inventory/items');
      const items = response.data;
      
      const products: InventoryProduct[] = (items || [])
        .filter((item: any) => item.isActive !== false)
        .map((item: any) => ({
          id: item.id?.toString() || '',
          name: item.name || '',
          barcode: item.barcode || '',
          stockCode: item.stockCode || '',
          quantity: item.quantity || 0,
          unit: item.unit || 'adet',
          salePrice: item.salePrice || 0,
          saleCurrency: item.saleCurrency || 'TRY',
          categoryId: item.categoryId?.toString() || null,
          brandId: item.brandId?.toString() || null,
          warehouseId: item.warehouseId?.toString() || null,
          isActive: item.isActive !== false,
          imageUrl: item.imageUrl || null,
          isMainItem: item.isMainItem !== false,
          parentItemId: item.parentItemId?.toString() || null
        }));
      
      console.log(`✅ [INVENTORY] ${products.length} stok ürünü yüklendi`);
      setInventoryProducts(products);
    } catch (error) {
      console.error('Stok ürünleri yüklenirken hata:', error);
      setInventoryProducts([]);
    } finally {
      setLoadingInventory(false);
    }
  };

  // ✅ Depolar artık AuthContext'ten otomatik geliyor

  // Ürün eşleştirme modalını aç
  const handleOpenMappingModal = async () => {
    if (selectedProducts.length === 0) {
      setError('Please select at least one product to match');
      return;
    }

    // Stok ürünlerini yükle (depolar zaten AuthContext'ten geliyor)
    await loadInventoryProducts();
    
    setIsMappingModalOpen(true);
  };

  // Ürün eşleştirme modalını kapat
  const handleCloseMappingModal = () => {
    setIsMappingModalOpen(false);
  };

  // Yeni ürün oluştur
  const createNewInventoryItem = (marketplaceProduct: MarketplaceProduct, selectedWarehouse: string, importImages: boolean, platform: string) => {
    return {
      name: marketplaceProduct.title,
      barcode: marketplaceProduct.barcode || '',
      stockCode: marketplaceProduct.sku || `${platform.toUpperCase()}-${marketplaceProduct.id}`,
      quantity: marketplaceProduct.stockQuantity,
      unit: 'adet',
      purchasePrice: Math.round(marketplaceProduct.price * 0.8 * 100) / 100, // %20 kar marjı varsayımı
      purchaseCurrency: marketplaceProduct.currency,
      salePrice: marketplaceProduct.price,
      saleCurrency: marketplaceProduct.currency,
      vatRate: 20, // Varsayılan KDV
      vatStatus: 'included',
      description: `Product imported from ${platform.toUpperCase()}. Category: ${marketplaceProduct.categoryName}, Brand: ${marketplaceProduct.brand}`,
      warehouseId: selectedWarehouse,
      isActive: marketplaceProduct.status === 'active',
      createdAt: new Date().toISOString(),
      brandId: null,
      categoryId: null,
      isMainItem: true,
      canBeNegative: false,
      imageUrl: importImages ? marketplaceProduct.imageUrl : null,
      // Pazaryeri özel alanları
      [`${platform}ProductId`]: marketplaceProduct.id,
      [`${platform}Brand`]: marketplaceProduct.brand,
      [`${platform}CategoryName`]: marketplaceProduct.categoryName,
      importedFrom: platform,
      importedAt: new Date().toISOString()
    };
  };

  // Alt ürün (variant) oluştur
  const createVariantInventoryItem = (marketplaceProduct: MarketplaceProduct, parentProduct: InventoryProduct, selectedWarehouse: string, importImages: boolean, platform: string) => {
    return {
      name: `${parentProduct.name} (${platform.toUpperCase()})`,
      barcode: marketplaceProduct.barcode || '',
      stockCode: marketplaceProduct.sku || `${platform.toUpperCase()}-${marketplaceProduct.id}`,
      quantity: marketplaceProduct.stockQuantity,
      unit: parentProduct.unit,
      purchasePrice: Math.round(marketplaceProduct.price * 0.8 * 100) / 100,
      purchaseCurrency: marketplaceProduct.currency,
      salePrice: marketplaceProduct.price,
      saleCurrency: marketplaceProduct.currency,
      vatRate: 20,
      vatStatus: 'included',
      description: `Child product imported from ${platform.toUpperCase()}. Parent product: ${parentProduct.name}`,
      warehouseId: selectedWarehouse,
      isActive: marketplaceProduct.status === 'active',
      createdAt: new Date().toISOString(),
      brandId: parentProduct.brandId,
      categoryId: parentProduct.categoryId,
      isMainItem: false, // Alt ürün
      parentItemId: parentProduct.id, // Ana ürüne bağla
      canBeNegative: false,
      imageUrl: importImages ? marketplaceProduct.imageUrl : parentProduct.imageUrl,
      // Pazaryeri özel alanları
      [`${platform}ProductId`]: marketplaceProduct.id,
      [`${platform}Brand`]: marketplaceProduct.brand,
      [`${platform}CategoryName`]: marketplaceProduct.categoryName,
      importedFrom: platform,
      importedAt: new Date().toISOString()
    };
  };

  // Eşleştirilen ürünleri içe aktar (SQL API'ye)
  const handleImportMappedProducts = async (mappings: any[]) => {
    setImporting(true);
    setError(null);

    try {
      let importedCount = 0;
      const results = [];
      
      for (const mapping of mappings) {
        try {
          const { marketplaceProduct, inventoryProduct, mappingType, selectedWarehouse, importImages } = mapping;
          
          if (mappingType === 'skip') {
            continue;
          }
          
          if (mappingType === 'new') {
            // Yeni ürün olarak ekle
            const inventoryItem = createNewInventoryItem(
              marketplaceProduct, 
              selectedWarehouse, 
              importImages, 
              integration.platform
            );
            
            const response = await accountingApi.post('/api/inventory/items', {
              ...inventoryItem
            });
            const savedItem = response.data;
            importedCount++;
            
            results.push({
              type: 'new',
              marketplaceId: marketplaceProduct.id,
              inventoryId: savedItem.id,
              success: true
            });
            
          } else if (mappingType === 'existing' && inventoryProduct) {
            // Mevcut ürünün alt ürünü olarak ekle
            const variantItem = createVariantInventoryItem(
              marketplaceProduct,
              inventoryProduct,
              selectedWarehouse,
              importImages,
              integration.platform
            );
            
            const variantResponse = await accountingApi.post('/api/inventory/items', {
              ...variantItem
            });
            const savedVariant = variantResponse.data;
            importedCount++;
            
            results.push({
              type: 'variant',
              marketplaceId: marketplaceProduct.id,
              parentInventoryId: inventoryProduct.id,
              variantInventoryId: savedVariant.id,
              success: true
            });
          }
          
        } catch (itemError) {
          console.error(`Ürün işleme hatası:`, itemError);
          results.push({
            marketplaceId: mapping.marketplaceProduct?.id,
            success: false,
            error: itemError instanceof Error ? itemError.message : 'Bilinmeyen hata'
          });
        }
      }
      
      // Başarı mesajı göster ve modalı kapat
      alert(t('marketplaceProductsModal.messages.productsImported', { count: importedCount }));
      setSelectedProducts([]);
      setIsMappingModalOpen(false);
      onClose();
      
    } catch (error) {
      console.error('Product import error:', error);
      setError(error instanceof Error ? error.message : t('marketplaceProductsModal.errors.importProducts'));
    } finally {
      setImporting(false);
    }
  };

  // Seçili ürünleri içe aktar (eski fonksiyon - basit içe aktarma için)
  const handleImportProducts = async () => {
    if (selectedProducts.length === 0) {
      setError('Please select at least one product to import');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const isN11 = integration.platform === 'n11';
      const isPttavm = integration.platform === 'pttavm';
      const basePath = integration.platform === 'trendyol'
        ? `/api/tyintegrations/${integration.platform}/import-products`
        : isN11
          ? '/api/n11/products/import'
          : isPttavm
            ? '/api/pttavm/products/import'
            : `/api/integrations/${integration.platform}/import-products`;

      const response = isN11
        ? await n11Api.post(basePath, {
            productIds: selectedProducts
          })
        : isPttavm
          ? await pttavmApi.post(basePath, {
              productIds: selectedProducts
            })
          : await nodeApi.post(basePath, {
        productIds: selectedProducts
      });
      const data = response.data;
      
      // Başarı mesajı göster ve modalı kapat
      alert(t('marketplaceProductsModal.messages.productsImported', { count: data.importedCount || selectedProducts.length }));
      setSelectedProducts([]);
      onClose();
    } catch (error) {
      console.error('Product import error:', error);
      setError(error instanceof Error ? error.message : t('marketplaceProductsModal.errors.importProducts'));
    } finally {
      setImporting(false);
    }
  };

  // Sayfalama
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sayfalanmış ürünler
  const paginatedProducts = filteredProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Durum chip'i
  const getStatusChip = (status: string) => {
    switch (status) {
      case 'active':
        return <Chip label={t('marketplaceProductsModal.status.active')} color="success" size="small" />;
      case 'inactive':
        return <Chip label={t('marketplaceProductsModal.status.inactive')} color="default" size="small" />;
      case 'out_of_stock':
        return <Chip label={t('marketplaceProductsModal.status.outOfStock')} color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: '80vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" component="div">
              {t('marketplaceProductsModal.title', { platform: platformInfo.name })}
            </Typography>
            <Chip 
              label={platformInfo.name}
              sx={{ 
                bgcolor: platformInfo.color,
                color: 'white',
                fontWeight: 'bold',
                ml: 2
              }}
              size="small"
            />
          </Box>
          <Box>
            {/* Yenile butonu filtrelere taşındı */}
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {integration.platform === 'trendyol' && (
          <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Trendyol MCP Uyumlu Hizli Kontroller
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={runCatalogLookupQuickCheck}
                disabled={catalogLoading}
                startIcon={catalogLoading ? <CircularProgress size={14} /> : <SyncIcon />}
              >
                Catalog Kontrolu
              </Button>
              <TextField
                size="small"
                label="Batch Request ID"
                value={batchRequestId}
                onChange={(e) => setBatchRequestId(e.target.value)}
                sx={{ minWidth: 280 }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={queryBatchResult}
                disabled={batchLoading}
                startIcon={batchLoading ? <CircularProgress size={14} /> : <SearchIcon />}
              >
                Batch Sonucu Sorgula
              </Button>
            </Box>
            {catalogSummary && (
              <Alert severity={catalogSummary.toLowerCase().includes('hata') ? 'warning' : 'success'} sx={{ mb: 1 }}>
                {catalogSummary}
              </Alert>
            )}
            {batchSummary && (
              <Alert severity={batchSummary.toLowerCase().includes('hata') ? 'warning' : 'info'}>
                {batchSummary}
              </Alert>
            )}
          </Paper>
        )}

        {/* Platform-Specific API Filtreleri */}
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterListIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              {t('marketplaceProductsModal.filters.title', { platform: platformInfo.name })}
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            {/* Ortak Filtreler */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                size="small"
                fullWidth
                label={t('marketplaceProductsModal.filters.productId')}
                value={filters.id}
                onChange={(e) => handleFilterChange('id', e.target.value)}
                placeholder={t('marketplaceProductsModal.filters.productId')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                size="small"
                fullWidth
                label={t('marketplaceProductsModal.filters.parentProductId')}
                value={filters.productMainId}
                onChange={(e) => handleFilterChange('productMainId', e.target.value)}
                placeholder={t('marketplaceProductsModal.filters.parentProductId')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                size="small"
                fullWidth
                label={t('marketplaceProductsModal.filters.stockCode')}
                value={filters.stockCode}
                onChange={(e) => handleFilterChange('stockCode', e.target.value)}
                placeholder={t('marketplaceProductsModal.filters.stockCode')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                size="small"
                fullWidth
                label={t('marketplaceProductsModal.filters.brandName')}
                value={filters.brandName}
                onChange={(e) => handleFilterChange('brandName', e.target.value)}
                placeholder={t('marketplaceProductsModal.filters.brandName')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                size="small"
                fullWidth
                label={t('marketplaceProductsModal.filters.barcode')}
                value={filters.barcode}
                onChange={(e) => handleFilterChange('barcode', e.target.value)}
                placeholder={t('marketplaceProductsModal.filters.barcode')}
              />
            </Grid>

            {/* N11 Özel Filtreleri */}
            {integration.platform === 'n11' && (
              <>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t('marketplaceProductsModal.filters.salesStatus')}</InputLabel>
                    <Select
                      value={filters.saleStatus}
                      onChange={(e) => handleFilterChange('saleStatus', e.target.value)}
                      label={t('marketplaceProductsModal.filters.salesStatus')}
                    >
                      <MenuItem value="">{t('marketplaceProductsModal.filters.all')}</MenuItem>
                      <MenuItem value="On_Sale">{t('marketplaceProductsModal.filters.onSale')}</MenuItem>
                      <MenuItem value="Not_On_Sale">{t('marketplaceProductsModal.filters.notOnSale')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t('marketplaceProductsModal.filters.productStatus')}</InputLabel>
                    <Select
                      value={filters.productStatus}
                      onChange={(e) => handleFilterChange('productStatus', e.target.value)}
                      label={t('marketplaceProductsModal.filters.productStatus')}
                    >
                      <MenuItem value="">{t('marketplaceProductsModal.filters.all')}</MenuItem>
                      <MenuItem value="Active">{t('marketplaceProductsModal.filters.active')}</MenuItem>
                      <MenuItem value="Passive">{t('marketplaceProductsModal.filters.passive')}</MenuItem>
                      <MenuItem value="Rejected">{t('marketplaceProductsModal.filters.rejected')}</MenuItem>
                      <MenuItem value="Waiting_For_Approval">{t('marketplaceProductsModal.filters.waitingForApproval')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    size="small"
                    fullWidth
                    label={t('marketplaceProductsModal.filters.categoryIds')}
                    value={filters.categoryIds}
                    onChange={(e) => handleFilterChange('categoryIds', e.target.value)}
                    placeholder={t('marketplaceProductsModal.filters.categoryIdsPlaceholder')}
                  />
                </Grid>
              </>
            )}

            {/* Trendyol Özel Filtreleri */}
            {integration.platform === 'trendyol' && (
              <>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    size="small"
                    fullWidth
                    label={t('marketplaceProductsModal.filters.productCode')}
                    value={filters.productCode}
                    onChange={(e) => handleFilterChange('productCode', e.target.value)}
                    placeholder={t('marketplaceProductsModal.filters.productCode')}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    size="small"
                    fullWidth
                    label={t('marketplaceProductsModal.filters.categoryId')}
                    value={filters.categoryId}
                    onChange={(e) => handleFilterChange('categoryId', e.target.value)}
                    placeholder={t('marketplaceProductsModal.filters.categoryId')}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t('marketplaceProductsModal.filters.approved')}</InputLabel>
                    <Select
                      value={filters.approved}
                      onChange={(e) => handleFilterChange('approved', e.target.value)}
                      label={t('marketplaceProductsModal.filters.approved')}
                    >
                      <MenuItem value="">{t('marketplaceProductsModal.filters.all')}</MenuItem>
                      <MenuItem value="true">{t('marketplaceProductsModal.filters.approved')}</MenuItem>
                      <MenuItem value="false">{t('marketplaceProductsModal.filters.notApproved')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t('marketplaceProductsModal.filters.archived')}</InputLabel>
                    <Select
                      value={filters.archived}
                      onChange={(e) => handleFilterChange('archived', e.target.value)}
                      label={t('marketplaceProductsModal.filters.archived')}
                    >
                      <MenuItem value="">{t('marketplaceProductsModal.filters.all')}</MenuItem>
                      <MenuItem value="true">{t('marketplaceProductsModal.filters.archived')}</MenuItem>
                      <MenuItem value="false">{t('marketplaceProductsModal.filters.active')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t('marketplaceProductsModal.filters.onSale')}</InputLabel>
                    <Select
                      value={filters.onSale}
                      onChange={(e) => handleFilterChange('onSale', e.target.value)}
                      label={t('marketplaceProductsModal.filters.onSale')}
                    >
                      <MenuItem value="">{t('marketplaceProductsModal.filters.all')}</MenuItem>
                      <MenuItem value="true">{t('marketplaceProductsModal.filters.onSale')}</MenuItem>
                      <MenuItem value="false">{t('marketplaceProductsModal.filters.notOnSale')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t('marketplaceProductsModal.filters.campaign')}</InputLabel>
                    <Select
                      value={filters.hasActiveCampaign}
                      onChange={(e) => handleFilterChange('hasActiveCampaign', e.target.value)}
                      label={t('marketplaceProductsModal.filters.campaign')}
                    >
                      <MenuItem value="">{t('marketplaceProductsModal.filters.all')}</MenuItem>
                      <MenuItem value="true">{t('marketplaceProductsModal.filters.campaign')}</MenuItem>
                      <MenuItem value="false">{t('marketplaceProductsModal.filters.noCampaign')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t('marketplaceProductsModal.filters.rejected')}</InputLabel>
                    <Select
                      value={filters.rejected}
                      onChange={(e) => handleFilterChange('rejected', e.target.value)}
                      label={t('marketplaceProductsModal.filters.rejected')}
                    >
                      <MenuItem value="">{t('marketplaceProductsModal.filters.all')}</MenuItem>
                      <MenuItem value="true">{t('marketplaceProductsModal.filters.rejected')}</MenuItem>
                      <MenuItem value="false">{t('marketplaceProductsModal.filters.notRejected')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t('marketplaceProductsModal.filters.blacklist')}</InputLabel>
                    <Select
                      value={filters.blacklisted}
                      onChange={(e) => handleFilterChange('blacklisted', e.target.value)}
                      label={t('marketplaceProductsModal.filters.blacklist')}
                    >
                      <MenuItem value="">{t('marketplaceProductsModal.filters.all')}</MenuItem>
                      <MenuItem value="true">{t('marketplaceProductsModal.filters.blacklisted')}</MenuItem>
                      <MenuItem value="false">{t('marketplaceProductsModal.filters.normal')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    size="small"
                    fullWidth
                    type="date"
                    label={t('marketplaceProductsModal.filters.startDate')}
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    size="small"
                    fullWidth
                    type="date"
                    label={t('marketplaceProductsModal.filters.endDate')}
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Next Page Token"
                    value={filters.nextPageToken}
                    onChange={(e) => handleFilterChange('nextPageToken', e.target.value)}
                    placeholder={trendyolNextPageToken || 'Sonraki token burada görünecek'}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Sıralama</InputLabel>
                    <Select
                      value={filters.orderByDirection}
                      onChange={(e) => handleFilterChange('orderByDirection', e.target.value)}
                      label="Sıralama"
                    >
                      <MenuItem value="">{t('marketplaceProductsModal.filters.all')}</MenuItem>
                      <MenuItem value="ASC">ASC</MenuItem>
                      <MenuItem value="DESC">DESC</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                size="small"
                fullWidth
                type="number"
                label={t('marketplaceProductsModal.filters.pageSize')}
                value={filters.size}
                onChange={(e) => handleFilterChange('size', e.target.value)}
                inputProps={{ min: 1, max: 100 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={12} md={integration.platform === 'trendyol' ? 12 : 7}>
              <Box sx={{ display: 'flex', gap: 1, height: '40px', alignItems: 'center' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<SearchIcon />}
                  onClick={applyFilters}
                  disabled={loading}
                  sx={{ minWidth: '120px' }}
                  title={integration.platform === 'trendyol' ? t('marketplaceProductsModal.tooltips.trendyolRateLimit') : ''}
                >
                  {t('marketplaceProductsModal.actions.fetchProducts')}
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={clearFilters}
                  disabled={loading}
                  sx={{ minWidth: '100px' }}
                >
                  {t('marketplaceProductsModal.actions.clear')}
                </Button>
                <Tooltip title={integration.platform === 'trendyol' ? t('marketplaceProductsModal.tooltips.refreshTrendyol') : t('marketplaceProductsModal.tooltips.refresh')}>
                  <IconButton onClick={applyFilters} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>
              {products.length === 0 ? t('marketplaceProductsModal.loading.testingAndLoading') : t('marketplaceProductsModal.loading.refreshing')}
            </Typography>
          </Box>
        ) : products.length === 0 && !error ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <FilterListIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {t('marketplaceProductsModal.emptyState.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('marketplaceProductsModal.emptyState.description')}
            </Typography>
            {integration.platform === 'trendyol' && (
              <Alert severity="warning" sx={{ mt: 2, maxWidth: 700, mx: 'auto' }}>
                <Typography variant="body2" gutterBottom>
                  <strong>{t('marketplaceProductsModal.trendyolLimits.title')}</strong>
                </Typography>
                <Typography variant="body2">
                  • {t('marketplaceProductsModal.trendyolLimits.maxPerMinute')}<br/>
                  • {t('marketplaceProductsModal.trendyolLimits.minInterval')}<br/>
                  • {t('marketplaceProductsModal.trendyolLimits.temporaryBlock')}<br/>
                  • {t('marketplaceProductsModal.trendyolLimits.securityMeasure')}
                </Typography>
              </Alert>
            )}
          </Box>
        ) : (
          <>
            {integration.platform === 'trendyol' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Toplam kayıt: {trendyolTotalElements ?? '-'} | Sonraki token: {trendyolNextPageToken || 'yok'}
              </Alert>
            )}
            {/* Arama ve İstatistikler */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder={t('marketplaceProductsModal.search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 300 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('marketplaceProductsModal.stats.total', { count: filteredProducts.length })}
                </Typography>
                {selectedProducts.length > 0 && (
                  <Typography variant="body2" color="primary.main" fontWeight="bold">
                    {t('marketplaceProductsModal.stats.selected', { count: selectedProducts.length })}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Ürün Tablosu */}
            <Paper elevation={1}>
              <TableContainer sx={{ maxHeight: '400px' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedProducts.length > 0 && selectedProducts.length < paginatedProducts.length}
                          checked={paginatedProducts.length > 0 && paginatedProducts.every(product => selectedProducts.includes(product.id))}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('marketplaceProductsModal.table.productName')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('marketplaceProductsModal.table.price')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('marketplaceProductsModal.table.stock')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('marketplaceProductsModal.table.category')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('marketplaceProductsModal.table.brand')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('marketplaceProductsModal.table.barcodeSku')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('marketplaceProductsModal.table.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedProducts.map((product) => (
                      <TableRow key={product.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedProducts.includes(product.id)}
                            onChange={() => handleSelectProduct(product.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {product.imageUrl && (
                              <Box
                                component="img"
                                src={product.imageUrl}
                                alt={product.title}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                  mr: 2
                                }}
                              />
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {product.title}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {Number(product.price ?? 0).toLocaleString(currentLocale)} {product.currency || 'TRY'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={product.stockQuantity > 0 ? 'text.primary' : 'error.main'}>
                            {product.stockQuantity}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {product.categoryName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {product.brand}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {product.barcode || product.sku || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {getStatusChip(product.status)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Sayfalama */}
              <TablePagination
                component="div"
                count={filteredProducts.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage={t('marketplaceProductsModal.pagination.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => t('marketplaceProductsModal.pagination.displayedRows', { from, to, count })}
              />
            </Paper>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose} disabled={importing}>
          {t('marketplaceProductsModal.actions.close')}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedProducts.length > 0 && (
            <>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={importing ? <CircularProgress size={20} /> : <LinkIcon />}
                onClick={handleOpenMappingModal}
                disabled={importing || selectedProducts.length === 0 || loadingInventory}
              >
                {loadingInventory ? t('marketplaceProductsModal.loading.loading') : t('marketplaceProductsModal.actions.matchProducts', { count: selectedProducts.length })}
              </Button>
              <Button 
                variant="outlined" 
                color="primary"
                startIcon={importing ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleImportProducts}
                disabled={importing || selectedProducts.length === 0}
              >
                {importing ? t('marketplaceProductsModal.loading.importing') : t('marketplaceProductsModal.actions.directImport')}
              </Button>
            </>
          )}
        </Box>
      </DialogActions>

      {/* Ürün Eşleştirme Modalı */}
      <ProductMappingModal
        open={isMappingModalOpen}
        onClose={handleCloseMappingModal}
        marketplaceProducts={products.filter(p => selectedProducts.includes(p.id))}
        inventoryProducts={inventoryProducts}
        warehouses={warehouses}
        platform={integration.platform}
        companyId={companyId}
        onImport={handleImportMappedProducts}
      />
    </Dialog>
  );
};

export default MarketplaceProductsModal;
